import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import Pagination, { EMPTY_PAGINATION, PaginationMeta, readPaginated } from './Pagination';
import styles from './styles/StockModals.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface Movimiento {
    id: number;
    producto: string;
    fecha: string;
    tipo: string;
    cantidad: number;
    motivo: string;
    usuario: string;
    pedido_id?: string | null;
    pedido_numero?: number | null;
}

interface HistorialStockModalProps {
    locales: Local[];
    onClose: () => void;
}

type TabKey = 'movimientos' | 'ventas' | 'ajustes';

const TABS: [TabKey, string][] = [
    ['movimientos', 'Movimientos'],
    ['ventas', 'Ventas'],
    ['ajustes', 'Ajustes'],
];

/** Un movimiento es de venta si su motivo lo marca como tal. */
const esVenta = (m: Movimiento) => /^Venta (Pedido|Promo)\b/.test(m.motivo || '');

/**
 * Motivos que genera un ajuste manual (los que ofrece el modal de Entrada /
 * Salida, mas las puestas a 0 y las entradas por compra). Lo que no es venta ni
 * ajuste queda solo en "Movimientos", que muestra todo.
 */
const MOTIVOS_AJUSTE = [
    'Entrada Manual', 'Salida Manual', 'Ajuste Inventario', 'Avería/Desperdicio',
    'Consumo Interno', 'Venta Manual', 'Otro', 'Puesta a 0', 'Reversion puesta a 0',
];

const esAjuste = (m: Movimiento) =>
    !esVenta(m) && (MOTIVOS_AJUSTE.includes(m.motivo) || /^Compra OC\b/.test(m.motivo || ''));

/**
 * Etiqueta legible del motivo.
 *
 * En los movimientos de venta el motivo guarda el id del pedido; se reemplaza
 * por el numero legible cuando se pudo resolver.
 */
const textoMotivo = (m: Movimiento) => {
    const venta = (m.motivo || '').match(/^(Venta (?:Pedido|Promo))\b/);
    if (!venta) return m.motivo;
    if (m.pedido_numero) return `${venta[1]} #${m.pedido_numero}`;
    if (m.pedido_id) return `${venta[1]} ${m.pedido_id}`;
    return `${m.motivo} (eliminado)`;
};

/**
 * Historial de stock: movimientos, ventas y ajustes.
 *
 * Se pide siempre el historial completo (include_sales) y las pestañas filtran
 * sobre ese mismo conjunto: son tres lecturas del mismo dato, no tres consultas
 * distintas.
 */
const HistorialStockModal: React.FC<HistorialStockModalProps> = ({ locales, onClose }) => {
    const [tab, setTab] = useState<TabKey>('movimientos');
    const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    const [busqueda, setBusqueda] = useState('');
    const [debounced, setDebounced] = useState('');
    const [idLocal, setIdLocal] = useState<number | ''>('');

    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(busqueda), 300);
        return () => window.clearTimeout(id);
    }, [busqueda]);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        const params = new URLSearchParams({
            include_sales: 'true',
            page: String(page),
            per_page: String(perPage),
        });
        if (debounced.trim()) params.set('producto', debounced.trim());
        if (idLocal) params.set('id_local', String(idLocal));

        try {
            const res = await fetch(`${API}/admin/stock/movements?${params.toString()}`);
            if (!res.ok) throw new Error('respuesta no ok');
            const { items, pagination } = readPaginated<Movimiento>(await res.json());
            setMovimientos(items);
            setMeta(pagination);
        } catch (err) {
            console.error('Error cargando historial de stock', err);
            setError('No se pudo cargar el historial.');
            setMovimientos([]);
        } finally {
            setCargando(false);
        }
    }, [page, perPage, debounced, idLocal]);

    useEffect(() => { cargar(); }, [cargar]);

    // Cambiar de filtro o de pestaña vuelve al principio: la pagina 3 de un
    // listado nuevo puede no existir.
    useEffect(() => { setPage(1); }, [debounced, idLocal, perPage, tab]);

    /*
     * El filtro por pestaña se aplica sobre la pagina ya traida, asi que el
     * contador de la paginacion cuenta movimientos de todos los tipos. Es el
     * mismo criterio que usa la tabla de Articulos y evita pedir el historial
     * entero solo para separarlo en tres.
     */
    const filas = useMemo(() => {
        if (tab === 'ventas') return movimientos.filter(esVenta);
        if (tab === 'ajustes') return movimientos.filter(esAjuste);
        return movimientos;
    }, [movimientos, tab]);

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className={`${styles.modal} ${styles.modalWide}`}
                role="dialog"
                aria-modal="true"
                aria-label="Historial de stock"
            >
                <div className={styles.modalHead}>
                    <div>
                        <h3>Historial de Stock</h3>
                        <p>Movimientos, ventas y ajustes sobre las existencias.</p>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
                        ×
                    </button>
                </div>

                <div className={styles.tabs} role="tablist">
                    {TABS.map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={tab === key}
                            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
                            onClick={() => setTab(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.histFilters}>
                        <div className={styles.searchBox}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="search"
                                value={busqueda}
                                onChange={e => setBusqueda(e.target.value)}
                                placeholder="Buscar por producto…"
                                aria-label="Buscar movimientos"
                            />
                        </div>

                        {locales.length > 1 && (
                            <select
                                value={idLocal}
                                onChange={e => setIdLocal(e.target.value ? Number(e.target.value) : '')}
                                aria-label="Sucursal"
                            >
                                <option value="">Todas las sucursales</option>
                                {locales.map(l => (
                                    <option key={l.locales_id} value={l.locales_id}>
                                        {l.nombre_local}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {error && <p className={styles.errorMsg}>{error}</p>}

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Producto</th>
                                    <th>Tipo</th>
                                    <th className={styles.num}>Cant.</th>
                                    <th>Motivo</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cargando && (
                                    <tr><td colSpan={6} className={styles.empty}>Cargando historial…</td></tr>
                                )}
                                {!cargando && filas.length === 0 && (
                                    <tr><td colSpan={6} className={styles.empty}>
                                        No hay movimientos para este filtro.
                                    </td></tr>
                                )}
                                {!cargando && filas.map(m => (
                                    <tr key={m.id}>
                                        <td className={styles.nowrap}>
                                            {new Date(m.fecha).toLocaleString('es-AR')}
                                        </td>
                                        <td>{m.producto}</td>
                                        <td>
                                            <span className={`${styles.badge} ${
                                                m.tipo === 'entrada' ? styles.badgeEntrada : styles.badgeSalida
                                            }`}>
                                                {m.tipo === 'entrada' ? 'Entrada' : 'Salida'}
                                            </span>
                                        </td>
                                        <td className={styles.num}>{m.cantidad}</td>
                                        <td title={m.pedido_id || undefined}>{textoMotivo(m)}</td>
                                        <td className={styles.muted}>{m.usuario}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        meta={meta}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                        itemLabel="movimientos"
                    />
                </div>
            </div>
        </div>
    );
};

export default HistorialStockModal;
